import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Calendar, Clock, Users, Shield, Check, X, Plus, Star, MessageCircle, ChevronRight, Coffee, TreePine, BookOpen, Building, Dumbbell, Sparkles, Image, Camera } from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { apiClient } from '../api/client';

const LOCATION_ICONS = {
  cafe: Coffee,
  park: TreePine,
  library: BookOpen,
  mall: Building,
  community_center: Building,
  sports_venue: Dumbbell,
  school: BookOpen,
  public: MapPin,
};

const STATUS_STYLES = {
  proposed: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Pending' },
  accepted: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Accepted' },
  checked_in: { bg: 'bg-green-100', text: 'text-green-700', label: 'In Progress' },
  completed: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Completed' },
  declined: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Declined' },
  cancelled: { bg: 'bg-red-100', text: 'text-red-600', label: 'Cancelled' },
};

const FriendMeetups = () => {
  const [meetups, setMeetups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ friend_email: '', title: '', location: '', location_type: 'cafe', proposed_date: '', proposed_time: '', notes: '' });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [feedbackId, setFeedbackId] = useState(null);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackNote, setFeedbackNote] = useState('');
  const [prepData, setPrepData] = useState(null);
  const [photoUrl, setPhotoUrl] = useState('');
  const [photoMeetupId, setPhotoMeetupId] = useState(null);

  const fetchMeetups = useCallback(async () => {
    try {
      const data = await apiClient.request('/api/meetups/');
      setMeetups(data.meetups || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchMeetups(); }, [fetchMeetups]);

  const handleCreate = async () => {
    if (!form.friend_email || !form.title || !form.location || !form.proposed_date || !form.proposed_time) {
      setError('Please fill in all required fields');
      return;
    }
    setCreating(true);
    setError('');
    try {
      await apiClient.request('/api/meetups/propose', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      setShowCreate(false);
      setForm({ friend_email: '', title: '', location: '', location_type: 'cafe', proposed_date: '', proposed_time: '', notes: '' });
      fetchMeetups();
    } catch (err) {
      setError(err.message || 'Failed to propose meetup');
    }
    setCreating(false);
  };

  const handleAction = async (meetupId, action) => {
    try {
      await apiClient.request(`/api/meetups/${meetupId}/${action}`, { method: 'POST' });
      fetchMeetups();
    } catch {}
  };

  const handleFeedback = async (meetupId) => {
    try {
      await apiClient.request(`/api/meetups/${meetupId}/feedback`, {
        method: 'POST',
        body: JSON.stringify({ rating: feedbackRating, note: feedbackNote }),
      });
      setFeedbackId(null);
      setFeedbackRating(0);
      setFeedbackNote('');
      fetchMeetups();
    } catch {}
  };

  const handleGetPrep = async (meetupId) => {
    try {
      const data = await apiClient.request(`/api/community/meetups/${meetupId}/prep`);
      setPrepData(data);
    } catch {}
  };

  const handleAddPhoto = async () => {
    if (!photoUrl.trim() || !photoMeetupId) return;
    try {
      await apiClient.request(`/api/community/meetups/${photoMeetupId}/photos`, {
        method: 'POST',
        body: JSON.stringify({ photo_url: photoUrl.trim(), caption: '' }),
      });
      setPhotoUrl('');
      setPhotoMeetupId(null);
      fetchMeetups();
    } catch {}
  };

  const upcoming = meetups.filter(m => ['proposed', 'accepted', 'checked_in'].includes(m.status));
  const past = meetups.filter(m => ['completed', 'declined', 'cancelled'].includes(m.status));

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-4xl mx-auto" data-testid="friend-meetups-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
              <MapPin size={28} className="text-teal-600" />
              Friend Meetups
            </h1>
            <p className="text-sm text-gray-500 mt-1">From practice to real connection</p>
          </div>
          <button
            onClick={() => setShowCreate(!showCreate)}
            className="flex items-center gap-1.5 px-4 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-600 text-white text-sm font-semibold rounded-xl hover:shadow-lg transition-all"
            data-testid="propose-meetup-btn"
          >
            <Plus size={16} />
            <span className="hidden sm:inline">Propose Meetup</span>
          </button>
        </div>

        {/* Safety Notice */}
        <div className="mb-6 p-3 sm:p-4 bg-teal-50 border border-teal-200 rounded-xl flex items-start gap-3" data-testid="safety-notice">
          <Shield size={20} className="text-teal-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-teal-900">Safety First</p>
            <p className="text-xs text-teal-700">All meetups are in public places. Parents are automatically notified for teens under 18.</p>
          </div>
        </div>

        {/* Create Panel */}
        {showCreate && (
          <div className="mb-6 p-5 bg-white rounded-2xl border-2 border-teal-200 shadow-sm" data-testid="create-meetup-panel">
            <h3 className="font-bold text-gray-900 mb-4">Propose a Meetup</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Friend&apos;s Email *</label>
                <input type="email" value={form.friend_email} onChange={e => setForm({ ...form, friend_email: e.target.value })} placeholder="friend@email.com" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500" data-testid="meetup-friend-email" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Meetup Title *</label>
                <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Coffee catchup" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500" data-testid="meetup-title" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Location *</label>
                <input type="text" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="e.g. Starbucks on Main St" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500" data-testid="meetup-location" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Location Type</label>
                <select value={form.location_type} onChange={e => setForm({ ...form, location_type: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500" data-testid="meetup-location-type">
                  <option value="cafe">Cafe</option>
                  <option value="park">Park</option>
                  <option value="library">Library</option>
                  <option value="mall">Mall</option>
                  <option value="community_center">Community Center</option>
                  <option value="sports_venue">Sports Venue</option>
                  <option value="school">School</option>
                  <option value="public">Other Public Place</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Date *</label>
                <input type="date" value={form.proposed_date} onChange={e => setForm({ ...form, proposed_date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500" data-testid="meetup-date" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Time *</label>
                <input type="time" value={form.proposed_time} onChange={e => setForm({ ...form, proposed_time: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500" data-testid="meetup-time" />
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-700 mb-1">Notes (optional)</label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Anything they should know..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500" rows={2} data-testid="meetup-notes" />
            </div>
            {error && <p className="text-sm text-red-600 mb-3" data-testid="meetup-error">{error}</p>}
            <div className="flex gap-2">
              <button onClick={() => setShowCreate(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={handleCreate} disabled={creating} className="flex-1 py-2 bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-semibold rounded-lg text-sm hover:shadow-md disabled:opacity-50" data-testid="send-meetup-btn">{creating ? 'Sending...' : 'Propose Meetup'}</button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading meetups...</div>
        ) : (
          <>
            {/* Upcoming */}
            {upcoming.length > 0 && (
              <div className="mb-6">
                <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">Upcoming</h2>
                <div className="space-y-3">
                  {upcoming.map(m => {
                    const LocationIcon = LOCATION_ICONS[m.location_type] || MapPin;
                    const status = STATUS_STYLES[m.status] || STATUS_STYLES.proposed;
                    return (
                      <div key={m.meetup_id} className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm" data-testid={`meetup-${m.meetup_id}`}>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center flex-shrink-0">
                              <LocationIcon size={20} className="text-teal-600" />
                            </div>
                            <div>
                              <p className="font-bold text-gray-900">{m.title}</p>
                              <p className="text-xs text-gray-500">with {m.friend_name || m.proposer_name}</p>
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${status.bg} ${status.text}`}>{status.label}</span>
                        </div>
                        <div className="flex flex-wrap gap-3 text-xs text-gray-600 mb-3">
                          <span className="flex items-center gap-1"><MapPin size={12} /> {m.location}</span>
                          <span className="flex items-center gap-1"><Calendar size={12} /> {m.proposed_date}</span>
                          <span className="flex items-center gap-1"><Clock size={12} /> {m.proposed_time}</span>
                        </div>
                        {m.safety_flags?.proposer_is_teen || m.safety_flags?.friend_is_teen ? (
                          <div className="flex items-center gap-1.5 text-xs text-teal-700 bg-teal-50 p-2 rounded-lg mb-3">
                            <Shield size={12} />
                            <span>Parent{m.safety_flags.parent_notified ? ' notified' : ' will be notified'}</span>
                          </div>
                        ) : null}
                        {m.status === 'proposed' && (
                          <div className="flex gap-2">
                            <button onClick={() => handleAction(m.meetup_id, 'decline')} className="flex-1 py-2 border border-gray-300 text-gray-600 font-medium rounded-lg text-sm" data-testid={`decline-meetup-${m.meetup_id}`}>Decline</button>
                            <button onClick={() => handleAction(m.meetup_id, 'accept')} className="flex-1 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold rounded-lg text-sm" data-testid={`accept-meetup-${m.meetup_id}`}>Accept</button>
                          </div>
                        )}
                        {m.status === 'accepted' && (
                          <div className="flex gap-2">
                            <button onClick={() => handleGetPrep(m.meetup_id)} className="flex-1 py-2 border border-violet-300 text-violet-600 font-medium rounded-lg text-sm flex items-center justify-center gap-1" data-testid={`prep-meetup-${m.meetup_id}`}>
                              <Sparkles size={14} /> AvA Prep
                            </button>
                            <button onClick={() => handleAction(m.meetup_id, 'check-in')} className="flex-1 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-semibold rounded-lg text-sm" data-testid={`checkin-meetup-${m.meetup_id}`}>
                              <span className="flex items-center justify-center gap-1"><Check size={14} /> I&apos;m Here</span>
                            </button>
                          </div>
                        )}
                        {m.status === 'checked_in' && (
                          <button onClick={() => handleAction(m.meetup_id, 'complete')} className="w-full py-2 bg-gradient-to-r from-emerald-500 to-green-600 text-white font-semibold rounded-lg text-sm" data-testid={`complete-meetup-${m.meetup_id}`}>
                            <span className="flex items-center justify-center gap-1"><Check size={14} /> Complete Meetup</span>
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Past */}
            {past.length > 0 && (
              <div className="mb-6">
                <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">Past Meetups</h2>
                <div className="space-y-2">
                  {past.map(m => {
                    const status = STATUS_STYLES[m.status] || STATUS_STYLES.completed;
                    const hasFeedback = m.feedback && m.feedback.length > 0;
                    return (
                      <div key={m.meetup_id} className="p-3 bg-gray-50 rounded-xl flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-700 truncate">{m.title}</p>
                          <p className="text-[10px] text-gray-400">{m.proposed_date} with {m.friend_name || m.proposer_name}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {m.status === 'completed' && !hasFeedback && (
                            <button onClick={() => setFeedbackId(m.meetup_id)} className="text-xs font-semibold text-teal-600 hover:text-teal-700" data-testid={`feedback-meetup-${m.meetup_id}`}>Rate</button>
                          )}
                          {m.status === 'completed' && (
                            <button onClick={() => { setPhotoMeetupId(m.meetup_id); setPhotoUrl(''); }} className="text-xs font-semibold text-violet-600 hover:text-violet-700 flex items-center gap-0.5" data-testid={`photo-meetup-${m.meetup_id}`}>
                              <Camera size={12} /> Photo
                            </button>
                          )}
                          <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${status.bg} ${status.text}`}>{status.label}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Feedback Modal */}
            {feedbackId && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl p-6 w-full max-w-sm" data-testid="meetup-feedback-modal">
                  <h3 className="font-bold text-gray-900 mb-3 text-center">How was the meetup?</h3>
                  <div className="flex justify-center gap-2 mb-4">
                    {[1, 2, 3, 4, 5].map(r => (
                      <button key={r} onClick={() => setFeedbackRating(r)} className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${r <= feedbackRating ? 'bg-amber-400 text-white scale-110' : 'bg-gray-100 text-gray-400'}`} data-testid={`feedback-star-${r}`}>
                        <Star size={18} fill={r <= feedbackRating ? 'white' : 'none'} />
                      </button>
                    ))}
                  </div>
                  <textarea value={feedbackNote} onChange={e => setFeedbackNote(e.target.value)} placeholder="How did it go? (optional)" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-4" rows={2} />
                  <div className="flex gap-2">
                    <button onClick={() => { setFeedbackId(null); setFeedbackRating(0); }} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm">Cancel</button>
                    <button onClick={() => handleFeedback(feedbackId)} disabled={feedbackRating === 0} className="flex-1 py-2 bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-semibold rounded-lg text-sm disabled:opacity-50" data-testid="submit-feedback-btn">Submit</button>
                  </div>
                </div>
              </div>
            )}

            {/* AvA Prep Modal */}
            {prepData && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl p-6 w-full max-w-md" data-testid="meetup-prep-modal">
                  <div className="text-center mb-4">
                    <Sparkles size={28} className="text-violet-500 mx-auto mb-2" />
                    <h3 className="text-lg font-bold text-gray-900">AvA Meetup Prep</h3>
                    <p className="text-xs text-gray-500">Meeting {prepData.friend_name} on {prepData.date} at {prepData.time}</p>
                  </div>
                  <div className="space-y-3 mb-4">
                    {prepData.tips.map((tip, i) => (
                      <div key={i} className="flex items-start gap-2 p-3 bg-violet-50 rounded-xl">
                        <span className="w-5 h-5 rounded-full bg-violet-200 text-violet-700 flex items-center justify-center text-xs font-bold flex-shrink-0">{i+1}</span>
                        <p className="text-sm text-gray-700">{tip}</p>
                      </div>
                    ))}
                    {prepData.personal_tip && (
                      <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-xl border border-amber-200">
                        <Star size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-amber-800">{prepData.personal_tip}</p>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-center text-gray-500 mb-4 italic">{prepData.encouragement}</p>
                  <button onClick={() => setPrepData(null)} className="w-full py-2.5 bg-gradient-to-r from-violet-500 to-purple-600 text-white font-semibold rounded-xl">Got it!</button>
                </div>
              </div>
            )}

            {/* Photo Upload Modal */}
            {photoMeetupId && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl p-6 w-full max-w-sm" data-testid="photo-upload-modal">
                  <h3 className="font-bold text-gray-900 mb-3 text-center flex items-center justify-center gap-2"><Camera size={18} className="text-violet-500" /> Add Memory Photo</h3>
                  <input type="url" value={photoUrl} onChange={e => setPhotoUrl(e.target.value)} placeholder="Paste photo URL..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-4" data-testid="photo-url-input" />
                  <div className="flex gap-2">
                    <button onClick={() => setPhotoMeetupId(null)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm">Cancel</button>
                    <button onClick={handleAddPhoto} disabled={!photoUrl.trim()} className="flex-1 py-2 bg-gradient-to-r from-violet-500 to-purple-600 text-white font-semibold rounded-lg text-sm disabled:opacity-50" data-testid="submit-photo-btn">Add Photo</button>
                  </div>
                </div>
              </div>
            )}

            {/* Empty State */}
            {meetups.length === 0 && !showCreate && (
              <div className="text-center py-16" data-testid="meetups-empty">
                <div className="w-16 h-16 mx-auto mb-4 bg-teal-100 rounded-2xl flex items-center justify-center">
                  <MapPin size={32} className="text-teal-500" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">No meetups yet</h3>
                <p className="text-sm text-gray-500 mb-4 max-w-xs mx-auto">
                  Ready to take your friendships offline? Propose a meetup at a public place!
                </p>
                <button onClick={() => setShowCreate(true)} className="px-6 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all">
                  Propose Your First Meetup
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default FriendMeetups;
